<template>
  <div class="vehicle-selector">
    <h3 class="text-xl font-bold mb-4">Add Vehicle Applications</h3>

    <!-- Tabs -->
    <div class="selector-tabs">
      <button
        @click="activeTab = 'individual'"
        :class="['tab-button', { active: activeTab === 'individual' }]"
      >
        <span class="tab-icon">🔍</span>
        Individual Vehicles
      </button>
      <button
        @click="activeTab = 'family'"
        :class="['tab-button', { active: activeTab === 'family' }]"
      >
        <span class="tab-icon">👥</span>
        By Family
      </button>
    </div>

    <!-- Family Tab -->
    <VehicleFamilySelector
      v-if="activeTab === 'family'"
      :unified="unified"
      :existing-applications="existingApplications"
      @family-added="onFamilyAdded"
    />

    <!-- Individual Tab -->
    <div v-else>
      <!-- Search-based Filter System -->
      <div class="filter-grid">
        <!-- Make Filter -->
        <div class="filter-item">
          <label>Make</label>
          <input
            v-model="filters.makeSearch"
            type="text"
            placeholder="Search make..."
            class="regular full bordered"
          />
          <div class="options-list">
            <div
              v-for="make in filteredMakes"
              :key="make.make"
              @click="selectMake(make)"
              class="option-item"
              :class="{
                selected: isSelected(selectedMakes, make.make, 'make')
              }"
            >
              {{ make.make }}
            </div>
          </div>
        </div>

        <!-- Model Filter -->
        <div class="filter-item">
          <label>Model ({{ availableModels.length }})</label>
          <input
            v-model="filters.modelSearch"
            type="text"
            placeholder="Search model..."
            class="regular full bordered"
            :disabled="selectedMakes.length === 0"
          />
          <div class="options-list">
            <div
              v-for="model in filteredModels"
              :key="model.model"
              @click="selectModel(model)"
              class="option-item"
              :class="{
                selected: isSelected(selectedModels, model.model, 'model')
              }"
            >
              {{ model.model }}
            </div>
          </div>
        </div>

        <!-- Year Filter -->
        <div class="filter-item">
          <label>Year ({{ availableYears.length }})</label>
          <input
            v-model="filters.yearSearch"
            type="text"
            placeholder="Search year..."
            class="regular full bordered"
            :disabled="selectedModels.length === 0"
          />
          <div class="options-list">
            <div
              v-for="year in filteredYears"
              :key="year.year"
              @click="selectYear(year)"
              class="option-item"
              :class="{
                selected: isSelected(selectedYears, year.year, 'year')
              }"
            >
              {{ year.year }}
            </div>
          </div>
        </div>
      </div>

      <!-- Selected Vehicles to Add -->
      <div v-if="vehicleOptions.length > 0" class="vehicles-section">
        <div class="section-header">
          <h4>
            Vehicles to Add: {{ selectedVehicles.length }} /
            {{ availableToAddCount }}
            <span v-if="alreadyAddedCount > 0" class="text-sm text-gray-500">
              ({{ alreadyAddedCount }} already added)
            </span>
          </h4>
          <div class="header-actions">
            <button @click="selectAllVehicles" class="btn-info-button">
              {{ allSelected ? "Deselect All" : "Select All Available" }}
            </button>
            <button
              @click="saveApplications"
              :disabled="selectedVehicles.length === 0 || saving"
              class="btn-success-button"
            >
              {{
                saving
                  ? "Saving..."
                  : `Add ${selectedVehicles.length} Applications`
              }}
            </button>
          </div>
        </div>

        <div class="vehicles-table-container">
          <table class="vehicles-table">
            <thead>
              <tr>
                <th width="50">
                  <input
                    type="checkbox"
                    :checked="allSelected"
                    @change="selectAllVehicles"
                  />
                </th>
                <th>Make</th>
                <th>Model</th>
                <th>Year</th>
                <th>Engine</th>
                <th width="100">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="vehicle in vehicleOptions"
                :key="vehicle.id"
                @click="
                  !isVehicleAlreadyAdded(vehicle) && toggleVehicle(vehicle)
                "
                :class="{
                  selected: isVehicleSelected(vehicle.id),
                  'already-added': isVehicleAlreadyAdded(vehicle)
                }"
                class="vehicle-row"
              >
                <td>
                  <input
                    type="checkbox"
                    :checked="isVehicleSelected(vehicle.id)"
                    :disabled="isVehicleAlreadyAdded(vehicle)"
                    @click.stop="
                      !isVehicleAlreadyAdded(vehicle) && toggleVehicle(vehicle)
                    "
                  />
                </td>
                <td>{{ vehicle.make }}</td>
                <td>{{ vehicle.model }}</td>
                <td>{{ vehicle.year }}</td>
                <td>
                  {{ vehicle.engineName }} {{ vehicle.engineLiter }}
                  {{ vehicle.aspiration }}
                </td>
                <td>
                  <span
                    v-if="isVehicleAlreadyAdded(vehicle)"
                    class="badge-already-added"
                    title="This vehicle is already added to the product"
                  >
                    ✓ Already Added
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div
        v-else-if="!loading && selectedYears.length > 0"
        class="text-center py-8 text-gray-500"
      >
        No vehicles found for selected filters
      </div>
    </div>
  </div>
</template>

<script>
import {
  getVehicleMakes,
  getModelsForMakes,
  getYearsForModels,
  getVehicleOptions
} from "@/api/store/VehiclesService";
import { addApplications } from "@/api/store/ApplicationsService";
import VehicleFamilySelector from "./VehicleFamilySelector.vue";

export default {
  name: "VehicleSelector",
  components: {
    VehicleFamilySelector
  },
  props: {
    unified: {
      type: String,
      required: true
    },
    existingApplications: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      activeTab: "individual",
      loading: false,
      saving: false,
      allMakes: [],
      selectedMakes: [],
      selectedModels: [],
      selectedYears: [],
      availableModels: [],
      availableYears: [],
      vehicleOptions: [],
      selectedVehicles: [],
      filters: {
        makeSearch: "",
        modelSearch: "",
        yearSearch: ""
      }
    };
  },
  computed: {
    filteredMakes() {
      if (!this.filters.makeSearch) return this.allMakes;
      return this.allMakes.filter(m =>
        m.make.toLowerCase().includes(this.filters.makeSearch.toLowerCase())
      );
    },
    filteredModels() {
      if (!this.filters.modelSearch) return this.availableModels;
      return this.availableModels.filter(m =>
        m.model.toLowerCase().includes(this.filters.modelSearch.toLowerCase())
      );
    },
    filteredYears() {
      if (!this.filters.yearSearch) return this.availableYears;
      return this.availableYears.filter(y =>
        y.year.toString().includes(this.filters.yearSearch)
      );
    },
    allSelected() {
      return (
        this.vehicleOptions.length > 0 &&
        this.selectedVehicles.length === this.vehicleOptions.length
      );
    },
    // Check if a vehicle already exists in the product's applications
    existingVehicleIds() {
      return new Set(
        this.existingApplications.map(
          app =>
            `${app.make}-${app.model}-${app.year}-${app.option ||
              app.engineName}`
        )
      );
    },
    availableToAddCount() {
      return this.vehicleOptions.filter(v => !this.isVehicleAlreadyAdded(v))
        .length;
    },
    alreadyAddedCount() {
      return this.vehicleOptions.filter(v => this.isVehicleAlreadyAdded(v))
        .length;
    }
  },
  watch: {
    selectedMakes: {
      handler() {
        this.loadModels();
      },
      deep: true
    },
    selectedModels: {
      handler() {
        this.loadYears();
      },
      deep: true
    },
    selectedYears: {
      handler() {
        this.loadVehicles();
      },
      deep: true
    }
  },
  mounted() {
    this.loadMakes();
  },
  methods: {
    async loadMakes() {
      this.loading = true;
      try {
        this.allMakes = await getVehicleMakes();
      } catch (error) {
        this.$toast.error("Error loading makes");
      } finally {
        this.loading = false;
      }
    },
    async loadModels() {
      if (this.selectedMakes.length === 0) {
        this.availableModels = [];
        this.selectedModels = [];
        return;
      }
      this.loading = true;
      try {
        const response = await getModelsForMakes(this.selectedMakes);
        this.availableModels = response.flatMap(m => m.models);
      } catch (error) {
        this.$toast.error("Error loading models");
      } finally {
        this.loading = false;
      }
    },
    async loadYears() {
      if (this.selectedModels.length === 0) {
        this.availableYears = [];
        this.selectedYears = [];
        return;
      }
      this.loading = true;
      try {
        const response = await getYearsForModels(this.selectedModels);
        this.availableYears = response.flatMap(m => m.years);
      } catch (error) {
        this.$toast.error("Error loading years");
      } finally {
        this.loading = false;
      }
    },
    async loadVehicles() {
      if (this.selectedYears.length === 0) {
        this.vehicleOptions = [];
        return;
      }
      this.loading = true;
      try {
        this.vehicleOptions = await getVehicleOptions(this.selectedYears);
      } catch (error) {
        this.$toast.error("Error loading vehicles");
      } finally {
        this.loading = false;
      }
    },
    selectMake(make) {
      const index = this.selectedMakes.findIndex(m => m.make === make.make);
      if (index === -1) {
        this.selectedMakes.push(make);
      } else {
        this.selectedMakes.splice(index, 1);
      }
    },
    selectModel(model) {
      const index = this.selectedModels.findIndex(m => m.model === model.model);
      if (index === -1) {
        this.selectedModels.push(model);
      } else {
        this.selectedModels.splice(index, 1);
      }
    },
    selectYear(year) {
      const index = this.selectedYears.findIndex(y => y.year === year.year);
      if (index === -1) {
        this.selectedYears.push(year);
      } else {
        this.selectedYears.splice(index, 1);
      }
    },
    toggleVehicle(vehicle) {
      const index = this.selectedVehicles.findIndex(v => v.id === vehicle.id);
      if (index === -1) {
        this.selectedVehicles.push(vehicle);
      } else {
        this.selectedVehicles.splice(index, 1);
      }
    },
    isVehicleSelected(id) {
      return this.selectedVehicles.some(v => v.id === id);
    },
    isVehicleAlreadyAdded(vehicle) {
      const vehicleKey = `${vehicle.make}-${vehicle.model}-${vehicle.year}-${vehicle.engineName}`;
      return this.existingVehicleIds.has(vehicleKey);
    },
    isSelected(array, value, key) {
      return array.some(item => item[key] === value);
    },
    selectAllVehicles() {
      if (this.allSelected) {
        this.selectedVehicles = [];
      } else {
        // Only select vehicles that are not already added
        this.selectedVehicles = this.vehicleOptions.filter(
          v => !this.isVehicleAlreadyAdded(v)
        );
      }
    },
    async saveApplications() {
      this.saving = true;
      try {
        await addApplications(this.unified, this.selectedVehicles);
        this.$toast.success(
          `${this.selectedVehicles.length} applications added successfully`
        );
        this.$emit("applications-added");
      } catch (error) {
        this.$toast.error("Error saving applications");
        console.error(error);
      } finally {
        this.saving = false;
      }
    },
    onFamilyAdded() {
      this.$emit("applications-added");
    }
  }
};
</script>

<style lang="scss" scoped>
.vehicle-selector {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1.5rem;
}

.selector-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e5e7eb;

  .tab-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    background: transparent;
    border: none;
    border-bottom: 3px solid transparent;
    cursor: pointer;
    font-weight: 500;
    color: #6b7280;
    transition: all 0.2s;
    margin-bottom: -2px;

    .tab-icon {
      font-size: 1.125rem;
    }

    &:hover {
      color: #374151;
      background: #f9fafb;
    }

    &.active {
      color: #3b82f6;
      border-bottom-color: #3b82f6;
      background: #eff6ff;
    }
  }
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.filter-item {
  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #374151;
  }

  input {
    margin-bottom: 0.5rem;
  }
}

.options-list {
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  max-height: 200px;
  overflow-y: auto;
}

.option-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f3f4f6;
  }

  &.selected {
    background: #dbeafe;
    color: #1e40af;
    font-weight: 500;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }
}

.vehicles-section {
  margin-top: 2rem;
  border-top: 2px solid #e5e7eb;
  padding-top: 1.5rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  h4 {
    font-size: 1.125rem;
    font-weight: 600;
  }

  .header-actions {
    display: flex;
    gap: 0.5rem;
  }
}

.vehicles-table-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
}

.vehicles-table {
  width: 100%;
  border-collapse: collapse;

  thead {
    position: sticky;
    top: 0;
    background: #111827;
    color: white;
    z-index: 10;

    th {
      padding: 0.75rem;
      text-align: left;
      font-weight: 600;
    }
  }

  tbody {
    tr {
      cursor: pointer;
      transition: background 0.15s;

      &:nth-child(even) {
        background: #f9fafb;
      }

      &:hover {
        background: #fef3c7;
      }

      &.selected {
        background: #dcfce7;
      }

      &.already-added {
        background: #f3f4f6;
        color: #6b7280;
        cursor: not-allowed;
        opacity: 0.7;

        &:hover {
          background: #f3f4f6;
        }
      }

      td {
        padding: 0.75rem;
        border-bottom: 1px solid #f3f4f6;
      }
    }
  }
}

.badge-already-added {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #059669;
  background: #d1fae5;
  border-radius: 0.25rem;
}
</style>
